// -----------------------------------------------------------------------------
//  Da prenotazioni e spostamenti interni a RIGHE UNIFORMI.
//
//  Le due entità non si somigliano quasi per niente: una ha un cliente e tre
//  collezioni di tratte, l'altra ha un'auto e un elenco di corse. Condividono
//  però le sole cose che servono per stare nella stessa agenda: QUANDO,
//  DA DOVE A DOVE, CHE STATO. Qui la normalizzazione sta in un posto solo.
//
//  ⚠️ TRAPPOLE DI NAMING (dal dominio Simba, vedi CLAUDE.md del backend):
//   - `booking.type_booking` vale 'booking' | 'restaurant' (il SERVIZIO);
//     `pickup/escort/delivery.type_booking` vale 'gone' | 'return' (la
//     DIREZIONE). Stesso nome, due significati.
//   - a schermo: «Notturno» (non restaurant), «Transfer» (non escort),
//     «Spostamento interno» (non journey).
//
//  ⚠️ UNA RIGA È UNA TRATTA, non una prenotazione: un pickup andata e ritorno
//  produce due righe. È così che si legge un'agenda — per momento, non per
//  pratica.
// -----------------------------------------------------------------------------

import { VEHICLE_LABELS } from './vehicleTypes';

const place = (p) =>
  p ? [p.address, p.street_number, p.city].filter(Boolean).join(' ').trim() : '';

const personName = (d) =>
  d ? [d.name, d.surname].filter(Boolean).join(' ').trim() : '';

/**
 * Stato di una PRENOTAZIONE.
 * L'enum ha 48 valori, quasi tutti passi intermedi della macchina a stati del
 * driver: qui interessa la famiglia, non il singolo passo. Il dettaglio per
 * esteso lo mostra la pagina del booking.
 */
export function bookingState(state) {
  if (state === 'end') return { kind: 'muted', label: 'Conclusa' };
  if (state === 'canceled') return { kind: 'danger', label: 'Annullata' };
  if (state === 'before') return { kind: 'attn', label: 'Non ancora confermata' };
  if (state === 'accepted') return { kind: 'ok', label: 'Accettata' };
  return { kind: 'active', label: 'In corso' };
}

/**
 * Avanzamento della corsa dall'app del driver (`route.execution_status`): si
 * vede al posto della pianificazione finché la corsa non è conclusa. Il
 * veicolo consegnato conta come corsa finita.
 */
const AVANZAMENTO = {
  IN_PARTENZA: { kind: 'active', label: 'Driver in partenza' },
  IN_VIAGGIO: { kind: 'active', label: 'Driver in viaggio' },
  ARRIVATO: { kind: 'active', label: 'Driver arrivato' },
  CONSEGNATO: { kind: 'muted', label: 'Veicolo consegnato' },
};

/** Stato di una corsa di spostamento interno: avanzamento del driver, se c'è. */
export function corsaState(r) {
  if (r?.status !== 'CONCLUSA' && r?.status !== 'ELIMINATA' && AVANZAMENTO[r?.execution_status]) {
    return AVANZAMENTO[r.execution_status];
  }
  return routeState(r?.status);
}

/** Stato di una corsa di spostamento interno (`route.status`). */
export function routeState(status) {
  if (status === 'CONCLUSA') return { kind: 'muted', label: 'Conclusa' };
  if (status === 'CONFERMATA') return { kind: 'ok', label: 'Confermata' };
  if (status === 'MODIFICATO') return { kind: 'attn', label: 'Modificata' };
  if (status === 'ELIMINATA') return { kind: 'danger', label: 'Eliminata' };
  return { kind: 'muted', label: 'Programmata' };
}

/**
 * Orari di una tratta di prenotazione.
 *
 * Sul PICKUP l'orario scelto dall'operatore è l'ARRIVO in sede (`date_partner`)
 * e la partenza da casa del cliente la calcola il planner; sul TRANSFER è il
 * contrario. La direzione decide quale delle due colonne è la partenza: è la
 * stessa regola del gestionale admin, dove è già in esercizio.
 */
function legTimes(leg, andata) {
  const a = andata ? leg.date_customer : leg.date_partner;
  const b = andata ? leg.date_partner : leg.date_customer;
  if (a && b) return { start: a, end: b };
  return { start: a || b || null, end: null };
}

/**
 * Il MEZZO della prenotazione. Sempre l'informazione principale della riga,
 * ma non è sempre la stessa cosa:
 *
 *  - PICKUP / NOTTURNO: si sposta l'auto del cliente, quindi targa e modello
 *    arrivano da `booking.car`;
 *  - TRANSFER: si accompagna una persona, e l'auto del cliente non c'entra —
 *    `carId` è NULL. Il mezzo è `vehicle_type` (navetta, pulmino, auto del
 *    partner) con il numero di passeggeri.
 *
 * Senza questo ramo la colonna «Auto» resterebbe un trattino proprio sul
 * servizio dove il mezzo è la scelta che conta.
 */
function mezzoDi(booking) {
  const c = booking.car;
  const cortesia = booking.loaner ? booking.targa_loaner || '' : '';

  if (c?.targa) {
    return {
      plate: c.targa,
      // `Car` non ha un campo colore: marca e modello sono tutto quello che c'è.
      carLabel: [c.marca, c.modello].filter(Boolean).join(' '),
      // Dell'auto di cortesia il booking conserva SOLO la targa
      // (`targa_loaner`): marca e modello non esistono a database.
      loanerPlate: cortesia,
    };
  }

  const mezzo = VEHICLE_LABELS[booking.vehicle_type] || '';
  const passeggeri = booking.passengers
    ? `${booking.passengers} passegger${booking.passengers === 1 ? 'o' : 'i'}`
    : '';
  return {
    plate: mezzo || '',
    carLabel: passeggeri,
    loanerPlate: cortesia,
  };
}

/**
 * Una prenotazione -> una riga per tratta.
 * Un booking senza tratte non sparisce: produce comunque una riga, altrimenti
 * scomparirebbe dall'agenda senza che nessuno se ne accorga.
 */
export function bookingToRows(booking) {
  const notturno = booking.type_booking === 'restaurant';
  const sede = place(booking.headquarter) || booking.headquarter?.name || 'Sede';
  const base = {
    kind: 'booking',
    id: booking.id,
    shortId: (booking.id || '').slice(0, 8),
    date: booking.data,
    // La sede serve al filtro della Home: gli spostamenti interni non ne hanno una.
    headquarterId: booking.headquarter?.id || '',
    sede,
    customer: personName(booking.customer) || 'Cliente',
    phone: booking.customer?.telephone_number || '',
    ...mezzoDi(booking),
    ...bookingStateOf(booking),
  };
  const out = [];

  (booking.pickup_bookings || []).forEach((leg, i) => {
    const andata = leg.type_booking !== 'return';
    const casa = place(leg.point);
    out.push({
      ...base,
      key: `${booking.id}-p${i}`,
      // Il notturno passa dal flusso pickup ma a schermo è un servizio suo.
      service: notturno ? 'restaurant' : booking.loaner ? 'pickup_loaner' : 'pickup',
      direction: andata ? 'gone' : 'return',
      from: andata ? casa : sede,
      to: andata ? sede : casa,
      waypoints: [],
      km: leg.simba_km ?? null,
      driver: personName(leg.driver_pickup),
      ...legTimes(leg, andata),
    });
  });

  (booking.escort_bookings || []).forEach((leg, i) => {
    const andata = leg.type_booking !== 'return';
    out.push({
      ...base,
      key: `${booking.id}-e${i}`,
      service: notturno ? 'restaurant' : 'escort',
      direction: andata ? 'gone' : 'return',
      from: place(leg.point_origin_escort),
      to: place(leg.point_destination_escort),
      waypoints: [],
      km: leg.simba_km ?? null,
      driver: personName(leg.driver_escort),
      ...legTimes(leg, andata),
    });
  });

  (booking.delivery_bookings || []).forEach((leg, i) => {
    const andata = leg.type_booking !== 'return';
    out.push({
      ...base,
      key: `${booking.id}-d${i}`,
      service: 'delivery',
      direction: andata ? 'gone' : 'return',
      from: place(leg.point_origin_delivery),
      to: place(leg.point_destination_delivery),
      waypoints: [],
      km: leg.simba_km ?? null,
      driver: personName(leg.driver_delivery),
      ...legTimes(leg, andata),
    });
  });

  if (!out.length) {
    out.push({
      ...base,
      key: booking.id,
      service: notturno ? 'restaurant' : 'pickup',
      direction: null,
      from: sede,
      to: '',
      waypoints: [],
      km: null,
      driver: '',
      start: booking.data,
      end: null,
    });
  }
  return out;
}

function bookingStateOf(booking) {
  const s = bookingState(booking.state);
  return { stateKind: s.kind, stateLabel: s.label };
}

/**
 * Cosa manca a una corsa di spostamento interno perché sia completa.
 *
 * Il partner inserisce i dati in due momenti: crea la giornata e poi completa
 * targa, veicolo o indirizzi. Una corsa a metà, in agenda, è indistinguibile
 * da una pronta — e chi smista se ne accorge solo aprendola. Stessa regola del
 * dettaglio dello spostamento, dove la corsa incompleta è già segnalata.
 */
export function datiMancantiCorsa(r) {
  const vuoto = (v) => v === null || v === undefined || String(v).trim() === '';
  const out = [];
  // Con la targa prova la targa non c'è ancora: non è un dato mancante.
  if (vuoto(r.targa) && r.immatricolato !== false) out.push('targa');
  if (vuoto(r.marca)) out.push('marca');
  if (vuoto(r.modello)) out.push('modello');
  if (!r.startPoint || vuoto(r.startPoint.address)) out.push('partenza');
  if (!r.endPoint || vuoto(r.endPoint.address)) out.push('arrivo');
  if (vuoto(r.dateStart)) out.push('orario');
  return out;
}

/**
 * Uno spostamento interno -> una riga per corsa.
 *
 * Qui non c'è un cliente: c'è un'auto che si muove fra due sedi. Il
 * qualificatore che conta è quante persone servono (`two_operators`).
 */
export function journeyToRows(journey) {
  return (journey.routes || [])
    .filter((r) => r.status !== 'ELIMINATA')
    .map((r, i) => {
      const s = corsaState(r);
      return {
        kind: 'journey',
        key: `${journey.id}-r${i}`,
        id: journey.id,
        shortId: (journey.id || '').slice(0, 8),
        service: 'journey',
        direction: null,
        operators: r.two_operators ? 2 : 1,
        date: journey.date,
        start: r.dateStart || null,
        end: r.dateEnd || null,
        plate: r.targa || '',
        carLabel: [r.marca, r.modello].filter(Boolean).join(' '),
        loanerPlate: '',
        customer: '',
        phone: '',
        from: place(r.startPoint),
        to: place(r.endPoint),
        waypoints: [],
        km: r.km ?? null,
        driver: personName(r.driver),
        jolly: personName(r.jolly_driver),
        stateKind: s.kind,
        stateLabel: s.label,
        mancanti: datiMancantiCorsa(r),
      };
    });
}

/**
 * Tutte le righe dell'agenda, ordinate per momento.
 * Le righe senza orario finiscono in fondo al loro giorno invece di
 * disordinare l'elenco.
 */
export function buildRows({ bookings = [], journeys = [] }) {
  const rows = [
    ...bookings.flatMap(bookingToRows),
    ...journeys.flatMap(journeyToRows),
  ];
  const t = (r) => {
    const v = r.start || r.date;
    const n = v ? new Date(v).getTime() : NaN;
    return Number.isNaN(n) ? Infinity : n;
  };
  return rows.sort((a, b) => t(a) - t(b));
}

/**
 * Dove porta il click su una riga: il dettaglio della sua PRENOTAZIONE (non
 * della tratta: il dettaglio mostra tutte le tratte insieme) o dello
 * spostamento interno. '' = la riga non si apre.
 *
 * ⚠️ La rotta dello spostamento vuole `:date` in forma `YYYY-MM-DD`, mentre
 * `journey.date` arriva dal server come datetime: si taglia la parte data. Non
 * si passa per un fuso orario apposta — ricostruire la data in locale la
 * sposterebbe di un giorno a seconda dell'ora.
 */
export function rottaDettaglio(r) {
  if (!r?.id) return '';
  if (r.kind === 'booking') return `/agenda/prenotazione/${r.id}`;
  const giorno = String(r.date || '').slice(0, 10);
  if (r.kind === 'journey' && giorno.length === 10) return `/detail-journey/${giorno}/${r.id}`;
  return '';
}

export default { buildRows, bookingToRows, journeyToRows, bookingState, routeState, rottaDettaglio };
